import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { orderBy } from "lodash";
import { useFragment } from "react-relay";

import { useAssignDraftMemberPackage } from "@/features/Packages/hooks/useAssignDraftMemberPackage";
import { useIsUserSignedIn } from "@/features/Session";
import { Button } from "@whoosh/design-system/src/components/controls";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner";
import { InfoIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { useCustomerHasActiveOrUpcomingMembership } from "@whoosh/features/src/Customer";
import { usePackageDisplayName } from "@whoosh/features/src/Packages";

import { StorePackageDetailFooter_package$key } from "./__generated__/StorePackageDetailFooter_package.graphql";
import { StorePackageDetailFooter_session$key } from "./__generated__/StorePackageDetailFooter_session.graphql";

type Props = {
  sessionRef?: StorePackageDetailFooter_session$key | null;
  packageRef?: StorePackageDetailFooter_package$key | null;
  onSignUpPress: () => void;
  onSuccessfulDraftAssignment: (
    memberPackageId: string,
    orderId: string
  ) => void;
};

export const StorePackageDetailFooter = ({
  sessionRef,
  packageRef,
  onSignUpPress,
  onSuccessfulDraftAssignment,
}: Props) => {
  const { showToast } = useToast();
  const pkg = useFragment(StorePackageDetailFooterFragmentString, packageRef);
  const session = useFragment(
    StorePackageDetailFooterSessionFragmentString,
    sessionRef
  );

  const isSignedIn = useIsUserSignedIn(session);
  const customerId = session?.currentClientProfile?.member?.id;
  const packageName = usePackageDisplayName(pkg);

  const isMembershipPackage = pkg?.__typename === "MembershipPackage";
  const customerHasActiveMembership =
    useCustomerHasActiveOrUpcomingMembership(session);

  const shouldShowMembershipWarning =
    isMembershipPackage && customerHasActiveMembership;

  const [assignDraftMemberPackage, isAssigning] = useAssignDraftMemberPackage();

  const onGetPress = () => {
    if (customerId && pkg?.id) {
      assignDraftMemberPackage(
        { packageId: pkg.id, memberId: customerId },
        (res) => {
          const memberPackageId =
            res?.assignDraftPackageToMember?.memberPackage?.id;
          const orders =
            res?.assignDraftPackageToMember?.memberPackage?.orders?.edges?.flatMap(
              (edge) => (edge?.node ? [edge.node] : [])
            ) || [];

          const firstOrder = orderBy(
            orders || [],
            ["packageDueDate"],
            ["asc"]
          )[0];

          onSuccessfulDraftAssignment(
            memberPackageId || "",
            firstOrder?.id || ""
          );
        },
        (error) => {
          if (
            error.message !== "MEMBER_HAS_AN_ACTIVE_MEMBERSHIP" &&
            error.message !== "Member has an active membership."
          ) {
            return;
          }

          setTimeout(() => {
            showToast({
              title: t`Unable to purchase membership`,
              description: t`You have an active or upcoming membership.`,
              semanticStyle: "error",
            });
          }, 10);
        }
      );
    }
  };

  return (
    <Box
      py={5}
      px={6}
      borderTopWidth={shouldShowMembershipWarning ? 0 : 1.5}
      borderColor="surface-dividers"
    >
      {shouldShowMembershipWarning ? (
        <InfoBanner
          leadingIcon={InfoIcon}
          message={t`You already have an active membership. To upgrade or downgrade, please contact your club directly.`}
          colorTheme="BLUE"
        />
      ) : isSignedIn ? (
        <Button
          accessibilityLabel={t`Get ${packageName}`}
          onPress={onGetPress}
          isLoading={isAssigning}
        >{`${t`Get`} ${packageName}`}</Button>
      ) : (
        <Button
          onPress={onSignUpPress}
          accessibilityLabel={t`Sign Up to Continue`}
          testID="store-package-detail-footer-sign-up-to-continue-button"
        >{t`Sign Up to Continue`}</Button>
      )}
    </Box>
  );
};

const StorePackageDetailFooterFragmentString = graphql`
  fragment StorePackageDetailFooter_package on Package {
    id
    __typename
    ...usePackageDisplayName_package
  }
`;

const StorePackageDetailFooterSessionFragmentString = graphql`
  fragment StorePackageDetailFooter_session on Session {
    ...useIsUserSignedIn_session
    ...useCustomerHasActiveOrUpcomingMembership_session
    currentClientProfile {
      member {
        ... on Customer {
          id
        }
      }
    }
  }
`;
